import React, { useState, useEffect, useMemo } from "react";
import Drawer from "react-modern-drawer";
import "react-modern-drawer/dist/index.css";
import { Icon } from "@iconify/react";
import { toast } from "react-toastify";
import {
  createFertilizer,
  updateFertilizer,
  getLanguages,
  translateData,
} from "../lib/api";

const FERTILIZER_CATEGORIES = [
  "Bulky Organic Manures",
  "Concentrated Organic Manures",
  "Straight Nitrogenous Fertilizers",
  "Straight Phosphatic Fertilizers",
  "Straight Potassic Fertilizers",
  "Complex & Compound Fertilizers",
  "Water Soluble Fertilizers",
];

const NUTRIENT_FIELDS = [
  { key: "n_pct", label: "Nitrogen (N %)", short: "N" },
  { key: "p2o5_pct", label: "Phosphate (P₂O₅ %)", short: "P₂O₅" },
  { key: "k2o_pct", label: "Potash (K₂O %)", short: "K₂O" },
  { key: "s_pct", label: "Sulfur (S %)", short: "S" },
  { key: "ca_pct", label: "Calcium (Ca %)", short: "Ca" },
  { key: "mg_pct", label: "Magnesium (Mg %)", short: "Mg" },
  { key: "zn_pct", label: "Zinc (Zn %)", short: "Zn" },
  { key: "b_pct", label: "Boron (B %)", short: "B" },
];

const INITIAL_FORM = {
  category: "",
  generic_name: "",
  brand_name: "",
  default_unit_for_price: "per kg",
  is_chloride_containing: false,
  n_pct: "",
  p2o5_pct: "",
  k2o_pct: "",
  s_pct: "",
  ca_pct: "",
  mg_pct: "",
  zn_pct: "",
  b_pct: "",
};

const languageCode = (language) => language?.GoogleLanguageCode || language?.LanguageCode || "";
const isEnglish = (language) => ["en", "en-us", "en-gb", "eng"].includes(languageCode(language).toLowerCase());
const getToken = () => localStorage.getItem("authToken") || localStorage.getItem("token");

const normalizeLanguages = (response) => {
  const list = response?.status === "success" ? response.data : response?.data || response;
  return (Array.isArray(list) ? list : []).filter(
    (lang) =>
      lang.IsLanguageActivated === true ||
      lang.IsLanguageActivated === 1 ||
      lang.IsActive === true ||
      lang.IsActive === 1 ||
      lang.IsActivated === true ||
      lang.IsActivated === 1
  );
};

const parseOtherLang = (raw, fallback) => {
  if (!raw) return fallback ? { en: fallback } : {};
  let obj = raw;
  if (typeof raw === "string") {
    try {
      obj = JSON.parse(raw);
    } catch {
      return fallback ? { en: fallback } : {};
    }
  }
  if (obj && typeof obj === "object" && !Array.isArray(obj)) {
    if (obj.generic_name && typeof obj.generic_name === "object") {
      obj = obj.generic_name;
    } else if (obj.brand_name && typeof obj.brand_name === "object") {
      obj = obj.brand_name;
    } else if (obj.category && typeof obj.category === "object") {
      obj = obj.category;
    }
    return { ...obj };
  }
  return fallback ? { en: fallback } : {};
};

const customStyles = `
  .add-fertilizer-drawer {
    height: 100vh;
    overflow-y: auto !important;
    padding: 24px;
    box-sizing: border-box;
  }

  /* Livestock / Crop Master style tabs */
  .fertilizer-tabs {
    display: flex;
    gap: 8px;
    flex-wrap: wrap;
    border-bottom: 1px solid #e5e7eb;
    padding-bottom: 12px;
    margin-bottom: 20px;
  }
  .fertilizer-tab {
    border: 1px solid #e5e7eb;
    background: #f9fafb;
    border-radius: 8px;
    padding: 8px 14px;
    font-weight: 600;
    cursor: pointer;
    transition: all .15s ease-in-out;
    color: #374151;
  }
  .fertilizer-tab:hover:not(:disabled) {
    background: #f3f4f6;
  }
  .fertilizer-tab.active {
    background: #e8f0fe;
    border-color: #1a73e8;
    color: #1a73e8;
  }
  .fertilizer-tab:disabled {
    cursor: not-allowed;
    opacity: .55;
  }

  /* Form & grid */
  .form-grid-container {
    display: grid !important;
    grid-template-columns: minmax(0, 1fr) minmax(0, 1fr) !important;
    gap: 1.25rem !important;
    width: 100% !important;
    margin-bottom: 1.25rem !important;
  }
  .fert-nutrient-grid-3 {
    display: grid;
    grid-template-columns: repeat(3, minmax(0, 1fr));
    gap: 12px;
    margin-bottom: 1rem;
    width: 100%;
  }
  .fert-nutrient-grid-2 {
    display: grid;
    grid-template-columns: repeat(2, minmax(0, 1fr));
    gap: 12px;
    margin-bottom: 1rem;
    width: 100%;
  }

  /* Inputs */
  .form-control, textarea, .form-select {
    width: 100% !important;
    max-width: 100% !important;
    min-width: 0 !important;
    border-radius: 8px !important;
    padding: 8px 12px !important;
    border: 1px solid #ced4da !important;
    background: #fff !important;
    font-size: 0.9rem !important;
  }
  .form-control:focus, .form-select:focus {
    border-color: #1a73e8 !important;
    box-shadow: 0 0 0 3px rgba(26, 115, 232, 0.15) !important;
  }
  .form-control.is-invalid, .form-select.is-invalid {
    border-color: #dc3545 !important;
  }
  .error-message { color: #dc3545; font-size: 0.78rem; margin-top: 4px; }

  /* Section cards */
  .fert-section {
    border: 1px solid #e2e8f0;
    border-radius: 10px;
    padding: 1.25rem;
    margin-bottom: 1.25rem;
    background: #ffffff;
    width: 100%;
  }
  .fert-section-title {
    font-weight: 600;
    font-size: 0.95rem;
    color: #1a73e8;
    margin-bottom: 1rem;
    display: flex;
    align-items: center;
    gap: 8px;
    flex-wrap: wrap;
  }
  .fert-subtitle {
    font-size: 0.72rem;
    text-transform: uppercase;
    letter-spacing: 0.03em;
    color: #6b7280;
    font-weight: 700;
    margin: 0.5rem 0 0.6rem;
    display: inline-block;
    padding: 3px 8px;
    border-radius: 4px;
    background: #f1f5f9;
    border: 1px solid #e2e8f0;
  }

  /* Nutrient input wrapper */
  .fert-nutrient-input {
    display: flex;
    align-items: stretch;
    border: 1px solid #ced4da;
    border-radius: 8px;
    overflow: hidden;
    background: #fff;
    transition: border-color .15s, box-shadow .15s;
    width: 100%;
  }
  .fert-nutrient-input:focus-within {
    border-color: #1a73e8;
    box-shadow: 0 0 0 3px rgba(26, 115, 232, 0.15);
  }
  .fert-nutrient-input.invalid {
    border-color: #dc3545;
    box-shadow: 0 0 0 3px rgba(220, 53, 69, 0.15);
  }
  .fert-nutrient-input input {
    flex: 1 1 auto;
    min-width: 0 !important;
    width: 100%;
    border: none !important;
    padding: 8px 10px;
    font-size: 0.9rem;
    text-align: center;
    font-weight: 500;
    color: #0f172a;
    outline: none !important;
    box-shadow: none !important;
    background: transparent !important;
  }
  .fert-nutrient-input .suffix {
    display: flex;
    align-items: center;
    padding: 0 10px;
    background: #f8fafc;
    border-left: 1px solid #e2e8f0;
    font-size: 0.78rem;
    color: #64748b;
    font-weight: 600;
    flex-shrink: 0;
  }

  /* Chloride toggle row */
  .fert-chloride-row {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 10px;
    padding: 10px 12px;
    border: 1px solid #e2e8f0;
    border-radius: 8px;
    background: #f8fafc;
    cursor: pointer;
    user-select: none;
    transition: border-color .15s, background .15s;
    min-height: 42px;
    width: 100%;
  }
  .fert-chloride-row:hover { border-color: #cbd5e1; background: #f1f5f9; }
  .fert-chloride-row.active { border-color: #fbbf24; background: #fffbeb; }

  /* Footer */
  .drawer-footer {
    display: flex;
    justify-content: center;
    gap: 12px;
    padding-top: 1.25rem;
    border-top: 1px solid #e5e7eb;
    margin-top: 1rem;
    flex-wrap: wrap;
  }
`;

const AddFertilizerMaster = ({ open, onClose, onSuccess, recordToEdit }) => {
  const [formData, setFormData] = useState(INITIAL_FORM);
  const [categoryOL, setCategoryOL] = useState({});
  const [genericNameOL, setGenericNameOL] = useState({});
  const [brandNameOL, setBrandNameOL] = useState({});
  const [languages, setLanguages] = useState([]);
  const [selectedCode, setSelectedCode] = useState("");
  const [mainTab, setMainTab] = useState("english");
  const [translationCompleted, setTranslationCompleted] = useState(false);
  const [loadingLanguages, setLoadingLanguages] = useState(false);
  const [translating, setTranslating] = useState(false);
  const [saving, setSaving] = useState(false);
  const [errors, setErrors] = useState({});

  const isEditMode = Boolean(recordToEdit);
  const canOpenOtherLanguages = isEditMode || translationCompleted;

  const englishLanguage = useMemo(
    () => languages.find(isEnglish) || languages[0],
    [languages]
  );
  const englishCode = languageCode(englishLanguage) || "en";
  const otherLanguages = useMemo(
    () => languages.filter((l) => !isEnglish(l)),
    [languages]
  );
  const activeLanguage =
    languages.find((l) => languageCode(l) === selectedCode) || otherLanguages[0] || englishLanguage;

  /* ---------------- Fetch dynamic system languages ---------------- */
  useEffect(() => {
    if (!open) return;
    let isMounted = true;
    setLoadingLanguages(true);

    getLanguages()
      .then((res) => {
        if (!isMounted) return;
        const active = normalizeLanguages(res);
        setLanguages(active);
        const english = active.find(isEnglish) || active[0];
        const nonEnglish = active.filter((l) => !isEnglish(l));
        if (nonEnglish.length > 0) {
          setSelectedCode(languageCode(nonEnglish[0]));
        } else if (english) {
          setSelectedCode(languageCode(english));
        }
      })
      .catch((err) => {
        console.error("Failed to load languages:", err);
      })
      .finally(() => {
        if (isMounted) setLoadingLanguages(false);
      });

    return () => {
      isMounted = false;
    };
  }, [open]);

  /* ---------------- Prefill / Reset Form ---------------- */
  useEffect(() => {
    if (open) {
      if (recordToEdit) {
        setFormData({
          category: recordToEdit.category || "",
          generic_name: recordToEdit.generic_name || "",
          brand_name: recordToEdit.brand_name || "",
          default_unit_for_price: recordToEdit.default_unit_for_price || "per kg",
          is_chloride_containing: Boolean(recordToEdit.is_chloride_containing),
          n_pct:
            recordToEdit.n_pct !== null && recordToEdit.n_pct !== undefined
              ? String(recordToEdit.n_pct)
              : "",
          p2o5_pct:
            recordToEdit.p2o5_pct !== null && recordToEdit.p2o5_pct !== undefined
              ? String(recordToEdit.p2o5_pct)
              : "",
          k2o_pct:
            recordToEdit.k2o_pct !== null && recordToEdit.k2o_pct !== undefined
              ? String(recordToEdit.k2o_pct)
              : "",
          s_pct:
            recordToEdit.s_pct !== null && recordToEdit.s_pct !== undefined
              ? String(recordToEdit.s_pct)
              : "",
          ca_pct:
            recordToEdit.ca_pct !== null && recordToEdit.ca_pct !== undefined
              ? String(recordToEdit.ca_pct)
              : "",
          mg_pct:
            recordToEdit.mg_pct !== null && recordToEdit.mg_pct !== undefined
              ? String(recordToEdit.mg_pct)
              : "",
          zn_pct:
            recordToEdit.zn_pct !== null && recordToEdit.zn_pct !== undefined
              ? String(recordToEdit.zn_pct)
              : "",
          b_pct:
            recordToEdit.b_pct !== null && recordToEdit.b_pct !== undefined
              ? String(recordToEdit.b_pct)
              : "",
        });

        const parsedCat = parseOtherLang(
          recordToEdit.category_other_lang,
          recordToEdit.category
        );
        if (recordToEdit.category && !parsedCat.en) {
          parsedCat.en = recordToEdit.category;
        }
        setCategoryOL(parsedCat);

        const parsedGen = parseOtherLang(
          recordToEdit.generic_name_other_lang,
          recordToEdit.generic_name
        );
        if (recordToEdit.generic_name && !parsedGen.en) {
          parsedGen.en = recordToEdit.generic_name;
        }
        setGenericNameOL(parsedGen);

        const parsedBrand = parseOtherLang(
          recordToEdit.brand_name_other_lang,
          recordToEdit.brand_name
        );
        if (recordToEdit.brand_name && !parsedBrand.en) {
          parsedBrand.en = recordToEdit.brand_name;
        }
        setBrandNameOL(parsedBrand);

        setTranslationCompleted(true);
        setMainTab("english");
        setErrors({});
      } else {
        setFormData(INITIAL_FORM);
        setCategoryOL({});
        setGenericNameOL({});
        setBrandNameOL({});
        setTranslationCompleted(false);
        setMainTab("english");
        setErrors({});
      }
    }
  }, [recordToEdit, open]);

  /* ---------------- Form field change handlers ---------------- */
  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: type === "checkbox" ? checked : value,
    }));
    if (errors[name]) setErrors((prev) => ({ ...prev, [name]: null }));
  };

  const handleCategoryChange = (e) => {
    const val = e.target.value;
    setFormData((prev) => ({ ...prev, category: val }));
    setCategoryOL((prev) => ({ ...prev, en: val }));
    if (errors.category) setErrors((prev) => ({ ...prev, category: null }));
  };

  const handleGenericNameChange = (e) => {
    const val = e.target.value;
    setFormData((prev) => ({ ...prev, generic_name: val }));
    setGenericNameOL((prev) => ({ ...prev, en: val }));
    if (errors.generic_name) setErrors((prev) => ({ ...prev, generic_name: null }));
  };

  const handleBrandNameChange = (e) => {
    const val = e.target.value;
    setFormData((prev) => ({ ...prev, brand_name: val }));
    setBrandNameOL((prev) => ({ ...prev, en: val }));
    if (errors.brand_name) setErrors((prev) => ({ ...prev, brand_name: null }));
  };

  const handleNutrientChange = (key, value) => {
    if (value !== "" && !/^\d*\.?\d*$/.test(value)) return;

    const num = Number(value);
    if (value !== "" && num > 100) {
      setErrors((prev) => ({ ...prev, [key]: "Cannot exceed 100%" }));
    } else {
      setErrors((prev) => ({ ...prev, [key]: null }));
    }

    setFormData((prev) => ({ ...prev, [key]: value }));
  };

  const toggleChloride = () => {
    setFormData((prev) => ({
      ...prev,
      is_chloride_containing: !prev.is_chloride_containing,
    }));
  };

  /* ---------------- Total nutrient % ---------------- */
  const totalNutrientPct = useMemo(() => {
    let sum = 0;
    for (const field of NUTRIENT_FIELDS) {
      const val = parseFloat(formData[field.key]);
      if (!isNaN(val) && val > 0) sum += val;
    }
    return Math.round(sum * 100) / 100;
  }, [formData]);

  /* ---------------- Validation ---------------- */
  const validateForm = () => {
    const errs = {};

    if (!formData.generic_name.trim()) {
      errs.generic_name = "Generic name is required";
    } else if (formData.generic_name.trim().length > 100) {
      errs.generic_name = "Generic name cannot exceed 100 characters";
    }

    if (formData.brand_name && formData.brand_name.trim().length > 150) {
      errs.brand_name = "Brand name cannot exceed 150 characters";
    }

    for (const field of NUTRIENT_FIELDS) {
      const val = formData[field.key];
      if (val !== "" && val !== null && val !== undefined) {
        const num = Number(val);
        if (isNaN(num)) errs[field.key] = "Must be a valid number";
        else if (num < 0) errs[field.key] = "Cannot be negative";
        else if (num > 100) errs[field.key] = "Cannot exceed 100%";
      }
    }

    setErrors(errs);
    return Object.keys(errs).length === 0;
  };

  /* ---------------- Translate English (Livestock/Crop style) ---------------- */
  const translateEnglish = async () => {
    if (!formData.generic_name.trim()) {
      toast.error("Generic name is required in English before translating");
      return;
    }

    if (languages.length === 0) {
      toast.error("Languages not loaded yet. Please try again.");
      return;
    }

    setTranslating(true);
    try {
      const source = {};
      if (formData.category) {
        source.Category = formData.category;
      }
      if (formData.generic_name.trim()) {
        source.GenericName = formData.generic_name.trim();
      }
      if (formData.brand_name && formData.brand_name.trim()) {
        source.BrandName = formData.brand_name.trim();
      }

      const token = getToken();
      const result = await translateData(source, token);
      const response = result?.data?.data || result?.data || result || {};

      const nextCategory = formData.category
        ? { en: formData.category, ...(categoryOL || {}) }
        : {};
      const nextGeneric = { en: formData.generic_name.trim(), ...(genericNameOL || {}) };
      const nextBrand = formData.brand_name?.trim()
        ? { en: formData.brand_name.trim(), ...(brandNameOL || {}) }
        : {};

      languages.forEach((lang) => {
        const code = languageCode(lang);
        if (isEnglish(lang)) return;

        if (formData.category) {
          const transCat =
            response?.Category?.[code] ||
            response?.Category?.[lang?.LanguageCode] ||
            response?.Category?.en ||
            formData.category;
          nextCategory[code] = transCat;
        }

        const transGen =
          response?.GenericName?.[code] ||
          response?.GenericName?.[lang?.LanguageCode] ||
          response?.GenericName?.en ||
          formData.generic_name.trim();
        nextGeneric[code] = transGen;

        if (formData.brand_name?.trim()) {
          const transBrand =
            response?.BrandName?.[code] ||
            response?.BrandName?.[lang?.LanguageCode] ||
            response?.BrandName?.en ||
            formData.brand_name.trim();
          nextBrand[code] = transBrand;
        }
      });

      if (formData.category) {
        setCategoryOL(nextCategory);
      }
      setGenericNameOL(nextGeneric);
      if (formData.brand_name?.trim()) {
        setBrandNameOL(nextBrand);
      }

      setTranslationCompleted(true);
      setMainTab("other");
      if (otherLanguages.length > 0) {
        setSelectedCode(languageCode(otherLanguages[0]));
      }
      toast.success("Translations generated successfully");
    } catch (err) {
      console.error("Translation error:", err);
      toast.error(err?.response?.data?.message || "Translation failed");
    } finally {
      setTranslating(false);
    }
  };

  /* ---------------- Build Payload ---------------- */
  const buildPayload = () => {
    const parseNum = (val) =>
      val !== "" && val !== null && !isNaN(Number(val)) ? Number(val) : null;

    // Clean category_other_lang map
    let cleanCategory = null;
    if (formData.category) {
      cleanCategory = {
        en: formData.category,
      };
      otherLanguages.forEach((lang) => {
        const code = languageCode(lang);
        const val = categoryOL[code];
        if (val && typeof val === "string" && val.trim()) {
          cleanCategory[code] = val.trim();
        }
      });
    }

    // Clean generic_name_other_lang map
    const cleanGeneric = {
      en: formData.generic_name.trim(),
    };
    otherLanguages.forEach((lang) => {
      const code = languageCode(lang);
      const val = genericNameOL[code];
      if (val && typeof val === "string" && val.trim()) {
        cleanGeneric[code] = val.trim();
      }
    });

    // Clean brand_name_other_lang map
    let cleanBrand = null;
    if (formData.brand_name && formData.brand_name.trim()) {
      cleanBrand = {
        en: formData.brand_name.trim(),
      };
      otherLanguages.forEach((lang) => {
        const code = languageCode(lang);
        const val = brandNameOL[code];
        if (val && typeof val === "string" && val.trim()) {
          cleanBrand[code] = val.trim();
        }
      });
    }

    return {
      category: formData.category || null,
      category_other_lang: cleanCategory,
      generic_name: formData.generic_name.trim(),
      generic_name_other_lang: cleanGeneric,
      brand_name: formData.brand_name.trim() || null,
      brand_name_other_lang: cleanBrand,
      default_unit_for_price: formData.default_unit_for_price,
      is_chloride_containing: Boolean(formData.is_chloride_containing),
      n_pct: parseNum(formData.n_pct),
      p2o5_pct: parseNum(formData.p2o5_pct),
      k2o_pct: parseNum(formData.k2o_pct),
      s_pct: parseNum(formData.s_pct),
      ca_pct: parseNum(formData.ca_pct),
      mg_pct: parseNum(formData.mg_pct),
      zn_pct: parseNum(formData.zn_pct),
      b_pct: parseNum(formData.b_pct),
    };
  };

  /* ---------------- Final Save / Submit Handler ---------------- */
  const handleFinalSave = async (e) => {
    if (e) e.preventDefault();

    if (!validateForm()) {
      setMainTab("english");
      toast.warning("Please resolve validation errors before saving.");
      return;
    }

    try {
      setSaving(true);
      const token = getToken();
      const payload = buildPayload();

      if (isEditMode) {
        const res = await updateFertilizer(recordToEdit.id, payload, token);
        toast.success(res?.message || "Fertilizer updated successfully (backup archived).");
      } else {
        const res = await createFertilizer(payload, token);
        toast.success(res?.message || "Fertilizer created successfully.");
      }

      onSuccess();
      onClose();
    } catch (err) {
      console.error("Error saving fertilizer:", err);
      toast.error(err?.response?.data?.message || err?.message || "Failed to save fertilizer record.");
    } finally {
      setSaving(false);
    }
  };

  /* ---------------- Nutrient input renderer ---------------- */
  const renderNutrientField = (nut) => {
    const hasError = Boolean(errors[nut.key]);
    return (
      <div key={nut.key} style={{ minWidth: 0 }}>
        <label
          className="form-label d-flex align-items-center justify-content-between mb-1 gap-1"
          style={{ fontSize: "0.8rem", color: "#334155", minWidth: 0 }}
        >
          <span className="fw-semibold text-truncate">{nut.label}</span>
          <span
            className="fw-semibold flex-shrink-0"
            style={{
              fontSize: "0.68rem",
              padding: "2px 7px",
              borderRadius: 4,
              background: "#f1f5f9",
              color: "#64748b",
              border: "1px solid #e2e8f0",
            }}
          >
            {nut.short}
          </span>
        </label>
        <div className={`fert-nutrient-input ${hasError ? "invalid" : ""}`}>
          <input
            type="text"
            inputMode="decimal"
            placeholder="0.0"
            value={formData[nut.key]}
            onChange={(e) => handleNutrientChange(nut.key, e.target.value)}
          />
          <span className="suffix">%</span>
        </div>
        {hasError && <div className="error-message">{errors[nut.key]}</div>}
      </div>
    );
  };

  return (
    <>
      <style>{customStyles}</style>

      <Drawer
        open={open}
        onClose={onClose}
        direction="right"
        size={900}
        style={{ zIndex: 1050 }}
        className="add-fertilizer-drawer"
      >
        <div className="d-flex justify-content-between align-items-center mb-20">
          <h4 className="mb-0">
            {isEditMode ? "Update Fertilizer Master" : "Add Fertilizer Master"}
          </h4>
          <button
            type="button"
            className="btn p-0 border-0 bg-transparent text-secondary-dark"
            onClick={onClose}
            disabled={saving || translating}
          >
            <Icon icon="lucide:x" style={{ fontSize: "1.5rem" }} />
          </button>
        </div>

        {/* ============================================================ */}
        {/* MAIN TABS: English vs Other languages */}
        {/* ============================================================ */}
        <div className="fertilizer-tabs">
          <button
            type="button"
            className={`fertilizer-tab ${mainTab === "english" ? "active" : ""}`}
            onClick={() => {
              setMainTab("english");
              setSelectedCode(englishCode);
            }}
          >
            English
          </button>

          <button
            type="button"
            className={`fertilizer-tab ${mainTab === "other" ? "active" : ""}`}
            disabled={!canOpenOtherLanguages}
            title={!canOpenOtherLanguages ? "Translate English content first" : "Other languages"}
            onClick={() => {
              if (!canOpenOtherLanguages) return;
              setMainTab("other");
              if (otherLanguages.length > 0 && !selectedCode) {
                setSelectedCode(languageCode(otherLanguages[0]));
              }
            }}
          >
            Other languages
          </button>
        </div>

        {/* ============================================================ */}
        {/* OTHER LANGUAGES SUB-TABS */}
        {/* ============================================================ */}
        {mainTab === "other" && (
          <div className="fertilizer-tabs">
            {otherLanguages.map((language) => {
              const code = languageCode(language);
              return (
                <button
                  type="button"
                  key={code}
                  className={`fertilizer-tab ${selectedCode === code ? "active" : ""}`}
                  onClick={() => setSelectedCode(code)}
                >
                  {language.LanguageName || language.LanguageCode}
                </button>
              );
            })}
          </div>
        )}

        {loadingLanguages ? (
          <div className="text-center py-4 text-secondary-light">
            <span className="spinner-border spinner-border-sm me-2" role="status" />
            Loading languages...
          </div>
        ) : (
          <form onSubmit={handleFinalSave}>
            {/* ============================================================ */}
            {/* TAB 1: ENGLISH CONTENT */}
            {/* ============================================================ */}
            {mainTab === "english" && (
              <>
                {/* SECTION 1: BASIC INFORMATION */}
                <div className="fert-section">
                  <div className="fert-section-title">
                    <Icon icon="solar:document-text-outline" style={{ fontSize: 20 }} />
                    1. Basic Information
                  </div>

                  {/* Category Dropdown */}
                  <div className="mb-20">
                    <label className="form-label">
                      Category
                    </label>
                    <select
                      name="category"
                      value={formData.category}
                      onChange={handleCategoryChange}
                      className="form-select"
                    >
                      <option value="">Select Category</option>
                      {FERTILIZER_CATEGORIES.map((cat) => (
                        <option key={cat} value={cat}>
                          {cat}
                        </option>
                      ))}
                    </select>
                  </div>

                  {/* Generic Name */}
                  <div className="mb-20">
                    <label className="form-label">
                      Generic Name <span className="text-danger-600">*</span>
                    </label>
                    <input
                      type="text"
                      name="generic_name"
                      value={formData.generic_name}
                      onChange={handleGenericNameChange}
                      placeholder="e.g. Urea 46%, Di-Ammonium Phosphate (DAP), MOP"
                      maxLength={100}
                      className={`form-control ${errors.generic_name ? "is-invalid" : ""}`}
                      autoFocus
                    />
                    {errors.generic_name && (
                      <div className="error-message">{errors.generic_name}</div>
                    )}
                  </div>

                  {/* Brand / Trade Name */}
                  <div className="mb-20">
                    <label className="form-label">Brand / Trade Name</label>
                    <input
                      type="text"
                      name="brand_name"
                      value={formData.brand_name}
                      onChange={handleBrandNameChange}
                      placeholder="e.g. IFFCO Neem Coated, YaraMila Complex"
                      maxLength={150}
                      className={`form-control ${errors.brand_name ? "is-invalid" : ""}`}
                    />
                    {errors.brand_name && (
                      <div className="error-message">{errors.brand_name}</div>
                    )}
                  </div>

                  {/* Price Unit & Chloride Row */}
                  <div className="form-grid-container">
                    <div style={{ minWidth: 0 }}>
                      <label className="form-label">
                        Default Price Unit <span className="text-danger-600">*</span>
                      </label>
                      <select
                        name="default_unit_for_price"
                        value={formData.default_unit_for_price}
                        onChange={handleChange}
                        className="form-select"
                      >
                        <option value="per kg">per kg</option>
                        <option value="per Ton">per Ton</option>
                      </select>
                      <small className="text-muted d-block mt-1" style={{ fontSize: "0.72rem" }}>
                        Used in recommendation calculations
                      </small>
                    </div>

                    <div style={{ minWidth: 0 }}>
                      <label className="form-label">Chloride Formulation</label>
                      <div
                        className={`fert-chloride-row ${formData.is_chloride_containing ? "active" : ""}`}
                        onClick={toggleChloride}
                      >
                        <div className="d-flex align-items-center gap-2" style={{ minWidth: 0 }}>
                          <Icon
                            icon={
                              formData.is_chloride_containing
                                ? "lucide:alert-triangle"
                                : "lucide:check-circle-2"
                            }
                            style={{
                              fontSize: 16,
                              color: formData.is_chloride_containing ? "#d97706" : "#16a34a",
                              flexShrink: 0,
                            }}
                          />
                          <span
                            className="fw-medium text-primary-light text-truncate"
                            style={{ fontSize: "0.85rem" }}
                          >
                            {formData.is_chloride_containing
                              ? "Contains Chloride"
                              : "Chloride Free"}
                          </span>
                        </div>
                        <div
                          style={{
                            width: 36,
                            height: 20,
                            borderRadius: 999,
                            background: formData.is_chloride_containing ? "#f59e0b" : "#cbd5e1",
                            position: "relative",
                            transition: "background .2s",
                            flexShrink: 0,
                          }}
                        >
                          <span
                            style={{
                              position: "absolute",
                              top: 2,
                              left: formData.is_chloride_containing ? 18 : 2,
                              width: 16,
                              height: 16,
                              borderRadius: "50%",
                              background: "#fff",
                              transition: "left .2s",
                              boxShadow: "0 1px 3px rgba(0,0,0,0.2)",
                            }}
                          />
                        </div>
                      </div>
                      <small className="text-muted d-block mt-1" style={{ fontSize: "0.72rem" }}>
                        Important for chloride-sensitive crops
                      </small>
                    </div>
                  </div>
                </div>

                {/* SECTION 2: NUTRIENT ANALYSIS */}
                <div className="fert-section">
                  <div className="fert-section-title">
                    <Icon icon="solar:test-tube-minimalistic-outline" style={{ fontSize: 20 }} />
                    2. Nutrient Analysis (%)
                    <span
                      className="ms-2 fw-semibold"
                      style={{
                        fontSize: "0.72rem",
                        padding: "3px 8px",
                        borderRadius: 4,
                        background: totalNutrientPct > 0 ? "#e8f0fe" : "#f1f5f9",
                        color: totalNutrientPct > 0 ? "#1a73e8" : "#64748b",
                        border: `1px solid ${totalNutrientPct > 0 ? "#c7dbfb" : "#e2e8f0"}`,
                      }}
                    >
                      Total Active: {totalNutrientPct}%
                    </span>
                  </div>

                  <p className="text-secondary-light mb-3" style={{ fontSize: "0.82rem" }}>
                    Enter active elemental percentage (0 – 100). Leave blank if not present.
                  </p>

                  {/* Primary Macronutrients */}
                  <div className="fert-subtitle">Primary Macronutrients</div>
                  <div className="fert-nutrient-grid-3">
                    {NUTRIENT_FIELDS.slice(0, 3).map(renderNutrientField)}
                  </div>

                  {/* Secondary Macronutrients */}
                  <div className="fert-subtitle">Secondary Macronutrients</div>
                  <div className="fert-nutrient-grid-3">
                    {NUTRIENT_FIELDS.slice(3, 6).map(renderNutrientField)}
                  </div>

                  {/* Micronutrients */}
                  <div className="fert-subtitle">Micronutrients</div>
                  <div className="fert-nutrient-grid-2">
                    {NUTRIENT_FIELDS.slice(6, 8).map(renderNutrientField)}
                  </div>
                </div>

                {/* English Tab Actions (Crop Master & Livestock style) */}
                <div className="d-flex justify-content-center gap-3 mt-4 pb-4">
                  <button
                    type="button"
                    className="border border-danger-600 text-danger-600 px-56 py-10 radius-8"
                    onClick={onClose}
                    disabled={saving || translating}
                  >
                    Cancel
                  </button>

                  <button
                    type="button"
                    className="btn btn-yellow px-56 py-12"
                    onClick={translateEnglish}
                    disabled={translating || !formData.generic_name.trim()}
                  >
                    {translating ? (
                      <>
                        <span className="spinner-border spinner-border-sm me-2" role="status" />
                        Translating...
                      </>
                    ) : (
                      "Translate"
                    )}
                  </button>

                  <button
                    type="submit"
                    className="btn btn-yellow px-56 py-12"
                    disabled={saving || translating || (!isEditMode && !translationCompleted)}
                  >
                    {saving ? (
                      <>
                        <span className="spinner-border spinner-border-sm me-2" role="status" />
                        {isEditMode ? "Updating..." : "Saving..."}
                      </>
                    ) : isEditMode ? (
                      "Update Fertilizer"
                    ) : (
                      "Save Fertilizer"
                    )}
                  </button>
                </div>
              </>
            )}

            {/* ============================================================ */}
            {/* TAB 2: OTHER LANGUAGES CONTENT */}
            {/* ============================================================ */}
            {mainTab === "other" && (
              <>
                <div className="fert-section">
                  {/* Category Translation */}
                  <div className="mb-20">
                    <label className="form-label">
                      Category ({activeLanguage?.LanguageName || selectedCode})
                    </label>
                    <input
                      className="form-control"
                      value={categoryOL[selectedCode] || ""}
                      onChange={(e) =>
                        setCategoryOL((prev) => ({
                          ...prev,
                          [selectedCode]: e.target.value,
                        }))
                      }
                      placeholder={`Enter category in ${activeLanguage?.LanguageName || selectedCode}`}
                    />
                  </div>

                  {/* Generic Name Translation */}
                  <div className="mb-20">
                    <label className="form-label">
                      Generic Name ({activeLanguage?.LanguageName || selectedCode}){" "}
                      <span className="text-danger-600">*</span>
                    </label>
                    <input
                      className="form-control"
                      value={genericNameOL[selectedCode] || ""}
                      onChange={(e) =>
                        setGenericNameOL((prev) => ({
                          ...prev,
                          [selectedCode]: e.target.value,
                        }))
                      }
                      placeholder={`Enter generic name in ${activeLanguage?.LanguageName || selectedCode}`}
                    />
                  </div>

                  {/* Brand Name Translation */}
                  <div className="mb-20">
                    <label className="form-label">
                      Brand / Trade Name ({activeLanguage?.LanguageName || selectedCode})
                    </label>
                    <input
                      className="form-control"
                      value={brandNameOL[selectedCode] || ""}
                      onChange={(e) =>
                        setBrandNameOL((prev) => ({
                          ...prev,
                          [selectedCode]: e.target.value,
                        }))
                      }
                      placeholder={`Enter brand name in ${activeLanguage?.LanguageName || selectedCode} (optional)`}
                    />
                  </div>
                </div>

                {/* Other Languages Actions */}
                <div className="d-flex justify-content-center gap-3 mt-4 pb-4">
                  <button
                    type="button"
                    className="border border-danger-600 text-danger-600 px-56 py-10 radius-8"
                    onClick={onClose}
                    disabled={saving || translating}
                  >
                    Cancel
                  </button>

                  <button
                    type="submit"
                    className="btn btn-yellow px-56 py-12"
                    disabled={saving || translating}
                  >
                    {saving ? (
                      <>
                        <span className="spinner-border spinner-border-sm me-2" role="status" />
                        {isEditMode ? "Updating..." : "Saving..."}
                      </>
                    ) : isEditMode ? (
                      "Update Fertilizer"
                    ) : (
                      "Save Fertilizer"
                    )}
                  </button>
                </div>
              </>
            )}
          </form>
        )}
      </Drawer>
    </>
  );
};

export default AddFertilizerMaster;